/** The contacts list — one list-valued record field (plan "Contacts card (D6)"): a person's
 *  email addresses, phone numbers and URLs, and which of them are the user's own email and
 *  phone. Email and phone are edited only through it, wherever the console shows a person
 *  (the user record's Contacts card; the booking record's Guest panel, O8).
 *
 *  - `ContactsView` — view mode, the FieldSlot's `display`: the stored rows, sorted by type
 *    then sequence; each row reads type · value (its value actions) · marker · tier ·
 *    verified · notifications · updated.
 *  - `ContactsWidget` — the edit-mode input (`BUILTIN_WIDGETS.contacts`): the same rows on
 *    the same geometry (every row reserves the 40 px control in both modes — the
 *    Zero-Re-Layout Rule), each kept / added / changed / removed against the stored list
 *    with its words, "was …" and its own revert; inline value, verified, notifications and
 *    tier controls; reorder within a type; remove (or restore); ☆ Make user email / phone.
 *    The choice question a removal raises ("Which contact becomes the user email?") sits
 *    under the row that raised it; an error sits under the row it concerns
 *    (`email_in_use` → "Belongs to user #123 · Open"), a conflict's suggested fix under the
 *    list (the record kind's `fixFor`).
 *  - `AddContactMenu` — the card header's "Add contact" (adding never moves what is above
 *    it).
 *  - `ContactsProvider` — what the record kind knows beside the value: the account's own
 *    email / phone (the divergence rows: an address that is not a contact, a duplicate, a
 *    verified flag that disagrees), each stored contact's OTA and dates, the value actions,
 *    the owner link, the view mode's one-step "Make user email now", and the row layout
 *    (`contactsLayout`, decided on the panel's EDIT-mode width so Edit re-lays nothing).
 *
 *  The edits themselves are `contactsModel.ts` (pure). */
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import ReplayIcon from '@mui/icons-material/Replay';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  type SvgIconProps,
  type Theme,
} from '@mui/material';
import { createContext, useContext, useEffect, useId, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { formatZoned } from '@/utils/zonedTime';

import { changeKey, parseRecordKey, type RecordKey } from '../../draft/changeSet';
import { useDraft, useDraftField } from '../../draft/DraftProvider';
import { anchorKey, type RaisedChoice } from '../../editor/choices';
import { useRecordEditor } from '../../editor/context';
import type { RecordFieldConflict, RecordWidget, RecordWidgetProps } from '../../editor/types';
import { ChoiceQuestion } from '../ChoiceQuestion';
import { ConflictFixes } from '../ConflictFixes';
import { PANEL_INSET_PX } from '../../panels/fieldGrid';

import {
  addContact,
  asContactsValue,
  CONTACT_TIERS,
  CONTACT_TYPES,
  contactListItems,
  contactProblems,
  CONTACTS_PROTOCOL,
  contactsDivergence,
  editContactValue,
  keepReason,
  makeUserAddress,
  moveBounds,
  moveContact,
  removeContact,
  restoreContact,
  revertContact,
  setContactEnabled,
  setContactTier,
  setContactVerified,
  TIER_LABEL,
  TYPE_LABEL,
  userAddressKey,
  type ContactListItem,
  type ContactRow,
  type ContactsAccount,
  type ContactsDivergence,
  type ContactsValue,
  type ContactTier,
  type ContactType,
  type UserAddressType,
} from './contactsModel';

// ---------------------------------------------------------------------------
// Context — what the record kind knows beside the value
// ---------------------------------------------------------------------------

export type ContactsLayout = 'wide' | 'stacked';

/** One line per contact from this much panel content; below it a row takes two lines. */
export const CONTACTS_WIDE_MIN_PX = 960;

/** The row layout for a panel `panelWidth` px wide IN EDIT MODE (0 = unmeasured: wide). */
export function contactsLayout(panelWidth: number): ContactsLayout {
  return panelWidth === 0 || panelWidth - PANEL_INSET_PX >= CONTACTS_WIDE_MIN_PX ? 'wide' : 'stacked';
}

/** A stored contact's facts the value does not carry (`GET /users/<id>/contacts/`). */
export interface ContactMeta {
  ota?: { id: number; name: string | null } | null;
  created_ts?: string | null;
  updated_ts?: string | null;
}

export interface ContactsContextValue {
  /** The account's own email / phone and their verified flags. */
  account?: ContactsAccount | null;
  /** Stored contact id → its OTA and dates. */
  meta?: ReadonlyMap<number, ContactMeta>;
  /** A value's actions (copy, mailto / tel, "Users with this contact"). */
  valueActions?: (type: ContactType, value: string) => ValueAction[];
  /** An `email_in_use` owner's page (default `/users/<id>`). */
  ownerHref?: (userId: number) => string;
  /** View mode: make a contact the user email / phone in one step (a clean draft); absent
   *  = not offered. */
  onMakeUserNow?: (row: ContactRow) => void;
  layout?: ContactsLayout;
}

const ContactsContext = createContext<ContactsContextValue>({});

export const ContactsProvider = ContactsContext.Provider;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** The control height every row reserves in both modes. */
const ROW_MIN_PX = 40;

const WIDE_COLUMNS = '76px minmax(160px, 1fr) 176px 124px 112px 112px 84px 96px';
const WIDE_AREAS = '"type value marker tier verified notify updated actions"';
const STACKED_COLUMNS = '76px minmax(0, 1fr) 96px';
const STACKED_AREAS = '"type value actions" ". details details"';

const CELL_SX = { display: 'flex', alignItems: 'center', minHeight: ROW_MIN_PX, minWidth: 0, gap: 0.5 } as const;

const CHANGED_TINT = (t: Theme) => alpha(t.palette.warning.main, 0.12);

/** A row is a reveal target (Reachability's "Contact ›"): focusable by script only, and
 *  ringed while a reveal has put the focus there (`data-revealed`). */
const ROW_TARGET_SX = {
  outline: 'none',
  borderRadius: 1,
  '&[data-revealed]:focus': { boxShadow: (t: Theme) => `inset 0 0 0 2px ${t.palette.primary.main}` },
} as const;

const TYPE_ICON: Record<ContactType, ComponentType<SvgIconProps>> = {
  email: MailOutlineIcon,
  phone: PhoneOutlinedIcon,
  url: LinkOutlinedIcon,
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Words({ icon: Icon, color, children }: { icon: ComponentType<SvgIconProps>; color?: string; children: ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Icon aria-hidden sx={{ fontSize: 16, color: color ?? 'text.secondary', flex: 'none' }} />
      <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {children}
      </Box>
    </Box>
  );
}

function Caption({
  icon: Icon,
  color = 'text.secondary',
  iconColor,
  children,
  ...rest
}: {
  icon: ComponentType<SvgIconProps>;
  color?: string;
  iconColor?: string;
  children: ReactNode;
  [data: `data-${string}`]: unknown;
}) {
  return (
    <Typography
      variant="caption"
      component="div"
      color={color}
      sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
      {...rest}
    >
      <Icon aria-hidden sx={{ fontSize: 14, mt: '2px', flex: 'none', color: iconColor ?? 'inherit' }} />
      <Box component="span" sx={{ minWidth: 0 }}>
        {children}
      </Box>
    </Typography>
  );
}

function Header() {
  const cell = (area: string, text: string) => (
    <Typography key={area} variant="caption" color="text.secondary" sx={{ gridArea: area, fontWeight: 600 }}>
      {text}
    </Typography>
  );
  return (
    <Box
      aria-hidden
      data-contacts-header
      sx={{ display: 'grid', gridTemplateColumns: WIDE_COLUMNS, gridTemplateAreas: WIDE_AREAS, columnGap: 1, pb: 0.25 }}
    >
      {cell('type', 'Type')}
      {cell('value', 'Contact')}
      {cell('marker', 'Role')}
      {cell('tier', 'Tier')}
      {cell('verified', 'Verified')}
      {cell('notify', 'Notifications')}
      {cell('updated', 'Updated')}
    </Box>
  );
}

function shortDate(ts: string | null | undefined): string {
  return ts ? formatZoned(ts, 'UTC', { weekday: false, time: false, zone: false }) : '—';
}

function UpdatedCell({ meta }: { meta: ContactMeta | undefined }) {
  if (!meta?.updated_ts && !meta?.created_ts) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const title = [
    meta.updated_ts ? `Updated ${formatZoned(meta.updated_ts, 'UTC')}` : null,
    meta.created_ts ? `created ${formatZoned(meta.created_ts, 'UTC')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Tooltip title={title} describeChild>
      <Typography variant="body2" color="text.secondary" tabIndex={0} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {shortDate(meta.updated_ts ?? meta.created_ts)}
      </Typography>
    </Tooltip>
  );
}

function TierText({ tier, meta }: { tier: ContactTier; meta: ContactMeta | undefined }) {
  if (tier === 'fake') {
    return (
      <Tooltip title="A placeholder address, not the person's" describeChild>
        <Box component="span" tabIndex={0}>
          <Words icon={ReportProblemOutlinedIcon}>Fake</Words>
        </Box>
      </Tooltip>
    );
  }
  const ota = tier === 'ota' ? meta?.ota?.name : null;
  return (
    <Typography variant="body2" component="span" color={tier === 'auto' ? 'text.secondary' : 'text.primary'}>
      {TIER_LABEL[tier]}
      {ota ? ` · ${ota}` : ''}
    </Typography>
  );
}

/** "User email" / "User phone" — navy outlined, icon and words; "· frozen" with a lock
 *  while booking systems no longer update it. */
function UserMarker({
  type,
  frozen,
  duplicate,
  tint,
}: {
  type: UserAddressType;
  frozen: boolean;
  duplicate: boolean;
  tint: boolean;
}) {
  const Icon = type === 'email' ? AlternateEmailIcon : ContactPhoneOutlinedIcon;
  return (
    <Box
      component="span"
      data-user-marker={type}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: '1px',
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 1,
        color: 'primary.main',
        typography: 'caption',
        fontWeight: 600,
        bgcolor: tint ? CHANGED_TINT : 'transparent',
        maxWidth: '100%',
      }}
    >
      <Icon aria-hidden sx={{ fontSize: 14 }} />
      {type === 'email' ? 'User email' : 'User phone'}
      {frozen && (
        <Tooltip title="Booking systems no longer update it">
          <Box component="span" tabIndex={0} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            · <LockOutlinedIcon aria-hidden sx={{ fontSize: 13 }} /> frozen
          </Box>
        </Tooltip>
      )}
      {duplicate && <Box component="span">· duplicate</Box>}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

interface ListProps {
  mode: 'view' | 'edit';
  /** What is shown: the stored value (view) or the draft's (edit). */
  value: ContactsValue;
  /** The stored value — the row states and the notes are read against it. */
  baseline: ContactsValue | null;
  frozen: Record<UserAddressType, boolean>;
  /** Edit mode only. */
  edit?: {
    set: (next: ContactsValue, label: string) => void;
    onFocus: () => void;
    onBlur: () => void;
    describedBy: string;
    conflicts: RecordFieldConflict[];
    choices: RaisedChoice[];
    /** The preview's normalised value of the change (phones as E.164). */
    planned: ContactsValue | null;
    /** The stored list the server holds now, when the entry is stale. */
    storedNow: ContactsValue | null;
  };
}

function ContactsList({ mode, value, baseline, frozen, edit }: ListProps) {
  const ctx = useContext(ContactsContext);
  const layout = ctx.layout ?? 'wide';
  const editing = mode === 'edit' && edit !== undefined;
  const items: ContactListItem[] = editing
    ? contactListItems(baseline, value)
    : value.contacts.map((row) => ({
        row,
        base: row,
        state: 'kept',
        valueChanged: false,
        verifiedChanged: false,
        enabledChanged: false,
        tierChanged: false,
      }));
  // Notes are the stored list's (the same in both modes); the missing rows follow the draft
  // — the user's address as the draft leaves it, never the saved one it replaces.
  const storedDivergence = contactsDivergence(baseline ?? value, ctx.account);
  const liveDivergence = editing ? contactsDivergence(value, ctx.account, baseline) : storedDivergence;
  const problems = editing ? contactProblems(value, baseline) : [];
  const keys = new Set(items.map((i) => i.row.key));
  const unplacedConflicts = (edit?.conflicts ?? []).filter((c) => !c.contact_key || !keys.has(c.contact_key));
  const placedChoiceKeys = new Set<string>();
  const choiceFor = (key: string) =>
    (edit?.choices ?? []).filter((c) => {
      const type: UserAddressType = c.option.key === 'user_phone' ? 'phone' : 'email';
      const hit = userAddressKey(value, type) === key;
      if (hit) placedChoiceKeys.add(c.option.key);
      return hit;
    });

  const rows = items.map((item) => (
    <ContactItem
      key={item.row.key}
      item={item}
      editing={editing}
      layout={layout}
      value={value}
      baseline={baseline}
      frozen={frozen}
      storedDivergence={storedDivergence}
      problems={problems.filter((p) => p.key === item.row.key).map((p) => p.message)}
      conflicts={(edit?.conflicts ?? []).filter((c) => c.contact_key === item.row.key)}
      choices={choiceFor(item.row.key)}
      edit={edit}
    />
  ));
  const unplacedChoices = (edit?.choices ?? []).filter((c) => !placedChoiceKeys.has(c.option.key));

  return (
    <Box data-contacts-list={mode} data-layout={layout} sx={{ width: '100%', minWidth: 0 }}>
      {layout === 'wide' && <Header />}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }} aria-label="Contacts">
        {rows}
        {liveDivergence.missing.map((m) => (
          <MissingRow key={`missing:${m.type}`} missing={m} editing={editing} layout={layout} value={value} baseline={baseline} edit={edit} />
        ))}
        {items.length === 0 && liveDivergence.missing.length === 0 && (
          <Box component="li" sx={{ ...CELL_SX, color: 'text.disabled', typography: 'body2' }}>
            No contacts
          </Box>
        )}
      </Box>
      {unplacedConflicts.map((c) => (
        <ConflictLine key={`${c.code}:${c.message}`} conflict={c} />
      ))}
      {unplacedChoices.map((c) => (
        <Box key={c.option.key} sx={{ mt: 0.5 }}>
          <ChoiceQuestion choice={c} />
        </Box>
      ))}
    </Box>
  );
}

function ConflictLine({ conflict }: { conflict: RecordFieldConflict }) {
  const ctx = useContext(ContactsContext);
  const owner = conflict.code === 'email_in_use' ? conflict.owner_user_id : null;
  return (
    <Caption icon={ErrorOutlineIcon} color="error" data-contact-error={conflict.code}>
      {owner ? (
        <>
          Belongs to user #{owner} ·{' '}
          <Link component={RouterLink} to={ctx.ownerHref?.(owner) ?? `/users/${owner}`} color="inherit">
            Open
          </Link>
        </>
      ) : (
        conflict.message
      )}
    </Caption>
  );
}

function wasText(item: ContactListItem): string {
  const base = item.base;
  if (!base) return '';
  const bits: string[] = [];
  if (item.valueChanged) bits.push(base.contact || '(empty)');
  if (item.verifiedChanged && !item.valueChanged) bits.push(base.verified ? 'verified' : 'unverified');
  if (item.enabledChanged) bits.push(`notifications ${base.enabled ? 'on' : 'off'}`);
  if (item.tierChanged) bits.push(TIER_LABEL[base.tier]);
  return bits.join(' · ');
}

function ContactItem({
  item,
  editing,
  layout,
  value,
  baseline,
  frozen,
  storedDivergence,
  problems,
  conflicts,
  choices,
  edit,
}: {
  item: ContactListItem;
  editing: boolean;
  layout: ContactsLayout;
  value: ContactsValue;
  baseline: ContactsValue | null;
  frozen: Record<UserAddressType, boolean>;
  storedDivergence: ContactsDivergence;
  problems: string[];
  conflicts: RecordFieldConflict[];
  choices: RaisedChoice[];
  edit: ListProps['edit'];
}) {
  const ctx = useContext(ContactsContext);
  const uid = useId();
  const { row, state } = item;
  const removed = state === 'removed';
  const meta = row.id !== null ? ctx.meta?.get(row.id) : undefined;
  const TypeIcon = TYPE_ICON[row.type];
  const label = row.contact.trim() || `new ${TYPE_LABEL[row.type].toLowerCase()}`;
  const userType: UserAddressType | null = row.type === 'url' ? null : row.type;
  const isUser = userType !== null && !removed && userAddressKey(value, userType) === row.key;
  const wasUser = userType !== null && baseline !== null && userAddressKey(baseline, userType) === row.key;
  const duplicate = storedDivergence.duplicates.has(row.key);
  const mismatch = storedDivergence.verifiedMismatch.get(row.key);
  const keep = editing ? keepReason(value, row.key) : null;
  const canEdit = editing && edit !== undefined && !removed;
  const set = (next: ContactsValue, l: string) => edit?.set(next, l);
  const actions = ctx.valueActions && row.contact.trim() ? ctx.valueActions(row.type, row.contact.trim()) : [];
  // A row just added and still empty is being typed: "Enter the …" waits until its input
  // has been left once (the ledger still says why Save is blocked).
  const [touched, setTouched] = useState(false);
  const shownProblems = row.id === null && row.contact.trim() === '' && !touched ? [] : problems;
  const invalid = shownProblems.length > 0 || conflicts.length > 0;

  // A row just added takes the focus — once the "Add contact" menu that added it closed.
  const inputRef = useRef<HTMLInputElement>(null);
  const [fresh] = useState(canEdit && row.id === null && row.contact === '');
  useEffect(() => {
    if (!fresh) return undefined;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [fresh]);

  // ---- cells ---------------------------------------------------------------
  const typeCell = (
    <Box sx={{ ...CELL_SX, gridArea: 'type', color: removed ? 'text.disabled' : 'text.primary', typography: 'body2' }}>
      <TypeIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
      {TYPE_LABEL[row.type]}
    </Box>
  );

  let valueNode: ReactNode;
  if (canEdit) {
    valueNode = (
      <TextField
        size="small"
        fullWidth
        value={row.contact}
        error={invalid}
        inputRef={inputRef}
        placeholder={row.type === 'email' ? 'name@example.com' : row.type === 'phone' ? '+44 7700 900123' : 'https://…'}
        onFocus={edit?.onFocus}
        onBlur={() => {
          setTouched(true);
          edit?.onBlur();
        }}
        onChange={(e) => set(editContactValue(value, row.key, e.target.value, baseline), `Edit ${TYPE_LABEL[row.type].toLowerCase()} ${label}`)}
        inputProps={{
          'aria-label': `${TYPE_LABEL[row.type]} contact`,
          'aria-describedby': `${uid}-caption ${edit?.describedBy ?? ''}`.trim(),
          autoComplete: 'off',
          spellCheck: false,
          type: row.type === 'email' ? 'email' : row.type === 'phone' ? 'tel' : 'url',
        }}
        sx={(state === 'added' || item.valueChanged) ? { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } } : undefined}
      />
    );
  } else if (removed) {
    valueNode = (
      <Box component="s" sx={{ color: 'text.secondary', typography: 'body2', overflowWrap: 'anywhere' }}>
        {row.contact}
      </Box>
    );
  } else {
    valueNode =
      actions.length > 0 ? (
        <Typography variant="body2" component="div" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <ValueActions actions={actions}>{row.contact}</ValueActions>
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {row.contact || '—'}
        </Typography>
      );
  }
  const valueCell = <Box sx={{ ...CELL_SX, gridArea: 'value' }}>{valueNode}</Box>;

  let marker: ReactNode = null;
  if (isUser && userType) {
    marker = <UserMarker type={userType} frozen={frozen[userType]} duplicate={duplicate} tint={editing && !wasUser} />;
  } else if (duplicate && userType && !removed) {
    marker = (
      <Typography variant="caption" color="text.secondary">
        Also the user {userType} · duplicate
      </Typography>
    );
  } else if (userType && !removed && row.enabled) {
    const word = userType === 'email' ? 'user email' : 'user phone';
    if (canEdit) {
      marker = (
        <Button
          size="small"
          startIcon={<StarBorderIcon />}
          onClick={() => set(makeUserAddress(value, row.key), `Make ${label} the ${word}`)}
          sx={{ py: 0, minHeight: 28, whiteSpace: 'nowrap' }}
          aria-label={`Make ${label} the ${word}`}
        >
          Make {word}
        </Button>
      );
    } else if (!editing && ctx.onMakeUserNow && row.contact.trim()) {
      const now = ctx.onMakeUserNow;
      marker = (
        <Button
          size="small"
          startIcon={<StarBorderIcon />}
          onClick={() => now(row)}
          sx={{ py: 0, minHeight: 28, whiteSpace: 'nowrap' }}
          aria-label={`Make ${label} the ${word} now`}
        >
          Make {word}
        </Button>
      );
    }
  }
  // In the stacked layout these five sit in the row's second line, a grid of their own.
  const area = (name: string) => (layout === 'wide' ? { gridArea: name } : {});
  const markerCell = <Box sx={{ ...CELL_SX, ...area('marker') }}>{marker}</Box>;

  const tierCell = (
    <Box sx={{ ...CELL_SX, ...area('tier') }}>
      {canEdit ? (
        <TextField
          select
          size="small"
          fullWidth
          value={row.tier}
          onChange={(e) => set(setContactTier(value, row.key, e.target.value as ContactTier), `${label} → ${TIER_LABEL[e.target.value as ContactTier]}`)}
          SelectProps={{ inputProps: { 'aria-label': `Tier of ${label}` } }}
          sx={item.tierChanged ? { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } } : undefined}
        >
          {CONTACT_TIERS.map((t) => (
            <MenuItem key={t} value={t}>
              {t === 'ota' && meta?.ota?.name ? `OTA · ${meta.ota.name}` : TIER_LABEL[t]}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TierText tier={row.tier} meta={meta} />
      )}
    </Box>
  );

  const verifiedCell = (
    <Box sx={{ ...CELL_SX, ...area('verified') }}>
      {canEdit ? (
        <FormControlLabel
          sx={{ ml: -0.5, mr: 0, borderRadius: 1, pr: 0.75, ...(item.verifiedChanged ? { bgcolor: CHANGED_TINT } : {}) }}
          control={
            <Checkbox
              size="small"
              checked={row.verified}
              onChange={(e) => set(setContactVerified(value, row.key, e.target.checked), `${e.target.checked ? 'Verify' : 'Unverify'} ${label}`)}
              inputProps={{ 'aria-label': `${label} verified` }}
            />
          }
          label={<Typography variant="body2">Verified</Typography>}
        />
      ) : row.verified ? (
        <Words icon={CheckCircleOutlineIcon} color="text.primary">
          Verified
        </Words>
      ) : (
        <Typography variant="body2" component="span" color="text.secondary">
          <Words icon={RemoveIcon}>Unverified</Words>
        </Typography>
      )}
    </Box>
  );

  const notifyCell = (
    <Box sx={{ ...CELL_SX, ...area('notify') }}>
      {canEdit ? (
        <Tooltip title={keep && row.enabled ? keep : ''}>
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', borderRadius: 1, pr: 0.75, ...(item.enabledChanged ? { bgcolor: CHANGED_TINT } : {}) }}
          >
            <Switch
              size="small"
              checked={row.enabled}
              disabled={Boolean(keep) && row.enabled}
              onChange={(e) =>
                set(
                  setContactEnabled(value, row.key, e.target.checked, baseline),
                  `Notifications ${e.target.checked ? 'on' : 'off'} · ${label}`,
                )
              }
              inputProps={{ 'aria-label': `Notifications to ${label}` }}
            />
            <Typography variant="body2" aria-hidden>
              {layout === 'wide' ? (row.enabled ? 'On' : 'Off') : row.enabled ? 'Notifications on' : 'Notifications off'}
            </Typography>
          </Box>
        </Tooltip>
      ) : row.enabled ? null : (
        <Words icon={NotificationsOffOutlinedIcon}>{layout === 'wide' ? 'Off' : 'Notifications off'}</Words>
      )}
    </Box>
  );

  const updatedCell = (
    <Box sx={{ ...CELL_SX, ...area('updated') }}>
      <UpdatedCell meta={meta} />
    </Box>
  );

  const bounds = moveBounds(value, row.key);
  const actionsCell = (
    <Box sx={{ ...CELL_SX, gridArea: 'actions', justifyContent: 'flex-end', gap: 0 }} data-part="row-actions">
      {editing && edit && removed && (
        <Tooltip title={`Restore ${label}`}>
          <IconButton size="small" aria-label={`Restore ${label}`} onClick={() => baseline && set(restoreContact(value, baseline, row.key), `Restore ${label}`)}>
            <ReplayIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
      {canEdit && (
        <>
          <Tooltip title={bounds.up ? `Move ${label} up` : ''}>
            <span>
              <IconButton size="small" aria-label={`Move ${label} up`} disabled={!bounds.up} onClick={() => set(moveContact(value, row.key, -1), `Move ${label} up`)}>
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={bounds.down ? `Move ${label} down` : ''}>
            <span>
              <IconButton size="small" aria-label={`Move ${label} down`} disabled={!bounds.down} onClick={() => set(moveContact(value, row.key, 1), `Move ${label} down`)}>
                <ArrowDownwardIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={keep ?? `Remove ${label}`}>
            <span>
              <IconButton
                size="small"
                aria-label={`Remove ${label}`}
                disabled={Boolean(keep)}
                onClick={() => set(removeContact(value, row.key, baseline), `Remove ${label}`)}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Box>
  );

  // ---- captions ----------------------------------------------------------
  const captions: ReactNode[] = [];
  if (editing && state === 'added') {
    captions.push(
      <Caption key="added" icon={AddCircleOutlineIcon} iconColor="warning.main" data-row-state="added">
        Added
      </Caption>,
    );
  }
  if (editing && state === 'changed') {
    captions.push(
      <Caption key="changed" icon={ReplayIcon} data-row-state="changed">
        was {wasText(item)} ·{' '}
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => baseline && set(revertContact(value, baseline, row.key), `Revert ${label}`)}
          aria-label={`Revert ${label}`}
        >
          Revert
        </Link>
      </Caption>,
    );
  }
  if (editing && removed) {
    captions.push(
      <Caption key="removed" icon={DeleteOutlineIcon} data-row-state="removed">
        Removed
      </Caption>,
    );
  }
  if (editing && isUser && !wasUser && userType && !frozen[userType]) {
    captions.push(
      <Caption key="freeze" icon={LockOutlinedIcon} data-caption="freeze">
        Becomes the {userType === 'email' ? 'user email' : 'user phone'} and freezes it: booking systems no longer update it
      </Caption>,
    );
  }
  const planned = edit?.planned ? edit.planned.contacts.find((r) => r.key === row.key) : undefined;
  if (editing && planned && row.type === 'phone' && planned.contact !== row.contact.trim() && !removed) {
    captions.push(
      <Caption key="saved-as" icon={InfoOutlinedIcon} data-caption="saved-as">
        Saved as {planned.contact}
      </Caption>,
    );
  }
  const stored = edit?.storedNow?.contacts.find((r) => r.key === row.key);
  const base = item.base;
  if (editing && edit?.storedNow && base && (!stored || JSON.stringify(stored) !== JSON.stringify(base))) {
    captions.push(
      <Caption key="stale" icon={SyncProblemIcon} iconColor="warning.main" data-caption="stale">
        {stored ? `Changed on the server: ${stored.contact}${stored.enabled ? '' : ' · notifications off'}` : 'Deleted on the server'}
      </Caption>,
    );
  }
  for (const p of shownProblems) {
    captions.push(
      <Caption key={`problem:${p}`} icon={ErrorOutlineIcon} color="error" data-contact-error="invalid">
        {p}
      </Caption>,
    );
  }
  for (const c of conflicts) captions.push(<ConflictLine key={`${c.code}:${c.message}`} conflict={c} />);
  if (duplicate && !removed) {
    captions.push(
      <Caption key="duplicate" icon={InfoOutlinedIcon} data-caption="duplicate">
        Another contact has the same {row.type === 'email' ? 'address' : 'number'} — the user’s own {row.type}
      </Caption>,
    );
  }
  if (mismatch && !removed) {
    captions.push(
      <Caption key="mismatch" icon={InfoOutlinedIcon} data-caption="verified-mismatch">
        The account marks its {mismatch.type} {mismatch.account ? 'verified' : 'unverified'}; the next contacts save sets it
        from this contact
      </Caption>,
    );
  }
  for (const c of choices) {
    captions.push(
      <Box key={`choice:${c.option.key}`} sx={{ mt: 0.5 }}>
        <ChoiceQuestion choice={c} />
      </Box>,
    );
  }

  const captionBox =
    captions.length > 0 ? (
      <Box id={`${uid}-caption`} sx={{ gridColumn: '2 / -1', display: 'grid', gap: 0.25, pb: 0.5 }}>
        {captions}
      </Box>
    ) : null;

  return (
    <Box
      component="li"
      data-contact={row.key}
      data-state={editing ? state : undefined}
      aria-label={`${TYPE_LABEL[row.type]} ${row.contact}`}
      tabIndex={-1}
      sx={{
        ...ROW_TARGET_SX,
        display: 'grid',
        gridTemplateColumns: layout === 'wide' ? WIDE_COLUMNS : STACKED_COLUMNS,
        gridTemplateAreas: layout === 'wide' ? WIDE_AREAS : STACKED_AREAS,
        columnGap: 1,
        borderTop: 1,
        borderColor: 'divider',
        py: 0.25,
      }}
    >
      {typeCell}
      {valueCell}
      {layout === 'wide' ? (
        <>
          {markerCell}
          {tierCell}
          {verifiedCell}
          {notifyCell}
          {updatedCell}
        </>
      ) : (
        <Box
          sx={{
            gridArea: 'details',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            columnGap: 1,
          }}
        >
          {markerCell}
          {tierCell}
          {verifiedCell}
          {notifyCell}
          {updatedCell}
        </Box>
      )}
      {actionsCell}
      {captionBox}
    </Box>
  );
}

/** The account's email / phone that no contact holds (F18): a muted row, "Add as contact"
 *  in edit mode — the new contact becomes the user's, with the account's verified flag. */
function MissingRow({
  missing,
  editing,
  layout,
  value,
  baseline,
  edit,
}: {
  missing: ContactsDivergence['missing'][number];
  editing: boolean;
  layout: ContactsLayout;
  value: ContactsValue;
  baseline: ContactsValue | null;
  edit: ListProps['edit'];
}) {
  const ctx = useContext(ContactsContext);
  const TypeIcon = TYPE_ICON[missing.type];
  const word = missing.type === 'email' ? 'User email' : 'User phone';
  const actions = ctx.valueActions?.(missing.type, missing.value) ?? [];
  const add = () => {
    const { value: next } = addContact(value, missing.type, {
      contact: missing.value,
      verified: missing.verified,
      asUser: true,
      baseline,
    });
    edit?.set(next, `Add ${missing.value} as a contact`);
  };
  return (
    <Box
      component="li"
      data-contact-missing={missing.type}
      tabIndex={-1}
      sx={{
        ...ROW_TARGET_SX,
        display: 'grid',
        gridTemplateColumns: layout === 'wide' ? WIDE_COLUMNS : STACKED_COLUMNS,
        gridTemplateAreas: layout === 'wide' ? WIDE_AREAS : STACKED_AREAS,
        columnGap: 1,
        borderTop: 1,
        borderColor: 'divider',
        py: 0.25,
        color: 'text.secondary',
      }}
    >
      <Box sx={{ ...CELL_SX, gridArea: 'type', typography: 'body2' }}>
        <TypeIcon aria-hidden sx={{ fontSize: 16 }} />
        {TYPE_LABEL[missing.type]}
      </Box>
      <Box sx={{ ...CELL_SX, gridArea: 'value', typography: 'body2' }}>
        {actions.length > 0 ? <ValueActions actions={actions}>{missing.value}</ValueActions> : missing.value}
      </Box>
      <Box
        sx={{
          ...CELL_SX,
          ...(layout === 'wide' ? { gridRow: 1, gridColumn: 'marker-start / updated-end' } : { gridArea: 'details' }),
        }}
      >
        <Words icon={InfoOutlinedIcon}>{word} · not in contacts</Words>
      </Box>
      <Box sx={{ ...CELL_SX, gridArea: 'actions', justifyContent: 'flex-end' }}>
        {editing && edit && (
          <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ py: 0, minHeight: 28, whiteSpace: 'nowrap' }}>
            Add
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Modes
// ---------------------------------------------------------------------------

function frozenOf(baselineOf: (rec: RecordKey, path: string) => unknown, rec: RecordKey): Record<UserAddressType, boolean> {
  return { email: baselineOf(rec, 'freeze_email') === true, phone: baselineOf(rec, 'freeze_phone') === true };
}

/** View mode: the stored list, as the FieldSlot's `display`. */
export function ContactsView({ rec, value }: { rec: RecordKey; value: unknown }) {
  const { baselineOf } = useDraft();
  const stored = asContactsValue(value);
  return <ContactsList mode="view" value={stored} baseline={stored} frozen={frozenOf(baselineOf, rec)} />;
}

/** The FieldSlot's edit-mode input for a `contacts` field. */
function ContactsInput(p: RecordWidgetProps) {
  const { rec, field } = p;
  const { entity, id } = parseRecordKey(rec);
  const editor = useRecordEditor();
  const { conflictsFor, raised, preview, pathOf } = editor;
  const path = pathOf(field);
  const draft = useDraftField(entity, id, path);
  const freezeEmail = useDraftField(entity, id, 'freeze_email');
  const freezePhone = useDraftField(entity, id, 'freeze_phone');
  const value = asContactsValue(p.value);
  const baseline = p.baseline === undefined ? null : asContactsValue(p.baseline);
  const planned = preview.data?.changes.find((c) => c.key === changeKey(rec, path))?.to;
  const anchor = anchorKey(rec, path);
  return (
    <>
      <ContactsList
      mode="edit"
      value={value}
      baseline={baseline}
      frozen={{ email: freezeEmail.value === true, phone: freezePhone.value === true }}
      edit={{
        set: (next, label) => p.onChange(next, { label }),
        onFocus: p.onFocus,
        onBlur: p.onBlur,
        describedBy: p.describedBy,
        conflicts: conflictsFor(rec, path),
        choices: raised.filter((c) => c.anchor === anchor),
        planned: planned === undefined ? null : asContactsValue(planned),
        storedNow: draft.stale ? asContactsValue(draft.stale.current) : null,
      }}
      />
      {/* A conflict's suggested fix (the Guest panel's email_in_use → "make that user the
          owner instead") — the list owns its errors, so the fixes sit under it too. */}
      <ConflictFixes rec={rec} path={path} />
    </>
  );
}

/** The `contacts` widget: its input, with the list's protocol (`CONTACTS_PROTOCOL`: its
 *  summary, row checks and ledger lines). */
export const ContactsWidget: RecordWidget = Object.assign(ContactsInput, CONTACTS_PROTOCOL);

/** The card header's "Add contact": a new email, phone or URL row at the end of its type
 *  (its value input takes the focus). Render it in edit mode while the field is editable.
 *  `label` names whose contact where the panel holds more than the person ("Add guest
 *  contact"). */
export function AddContactMenu({
  entity,
  id,
  path = 'contacts',
  label = 'Add contact',
}: {
  entity: string;
  id: number;
  path?: string;
  label?: string;
}) {
  const draft = useDraftField(entity, id, path);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const add = (type: ContactType) => {
    setAnchor(null);
    const current = asContactsValue(draft.value);
    const baseline = draft.baseline === undefined ? null : asContactsValue(draft.baseline);
    draft.set(addContact(current, type, { baseline }).value, { label: `Add ${TYPE_LABEL[type].toLowerCase()} contact` });
  };
  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={anchor ? true : undefined}
        onClick={(e) => setAnchor(e.currentTarget)}
        data-add-contact
      >
        {label}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        // The new row's input takes the focus, not the button the menu came from.
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {CONTACT_TYPES.map((t) => {
          const Icon = TYPE_ICON[t];
          return (
            <MenuItem key={t} onClick={() => add(t)} data-add-contact-type={t} sx={{ gap: 1 }}>
              <Icon aria-hidden sx={{ fontSize: 18, color: 'text.secondary' }} />
              {TYPE_LABEL[t]}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
